import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Modal,
  Grid,
  CardActions,
  MenuItem,
  TableHead,
  TableRow,
  Select,
  CardMedia,
  Button,
  Divider,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material'

import { Download } from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import api from '../../config/api'
import { API_URL } from '../../config/config'

const OrderHistoryElement = () => {
  const [openModal, setOpenModal] = useState(false)
  const [product, setProduct] = useState({})

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { id: cart_id } = useParams()

  const [orderData, setOrderData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const options = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }
    return new Date(dateString).toLocaleDateString('ru-RU', options)
  }

  useEffect(() => {
    if (!cart_id) {
      setError('ID корзины не указан')
      setLoading(false)
      return
    }

    const fetchCartData = async () => {
      try {
        setLoading(true)
        const response = await api.post('/API_FRONT/cart/items/get', {
          id: cart_id,
        })

        if (response.data) {
          setOrderData({
            id: response.data.id || cart_id,
            date_create: response.data.date,
            status: response.data.status || 'Новый заказ🔥',
            total: response.data.total || 0,
            date_of_arraving: response.data.arrivalDate,
            items: response.data.items || [],
            excelUrl: `${API_URL}/API_FRONT/user/files/cart/${response.data.id}/`,
          })
        }
      } catch (err) {
        console.error('Ошибка при загрузке данных корзины:', err)
        setError('Не удалось загрузить данные корзины')
      } finally {
        setLoading(false)
      }
    }

    fetchCartData()
  }, [cart_id])

  if (!cart_id) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">ID корзины не указан</Typography>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  if (!orderData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Данные корзины не найдены</Typography>
      </Box>
    )
  }

  const handleRowClick = (item) => {
    setProduct(item) // сохраняем выбранный элемент
    setOpenModal(true) // открываем модальное окно
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: isMobile ? 1 : 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            mb: 2,
          }}
        >
          <Box sx={{ p: 2, textAlign: 'left' }}>
            {' '}
            {/* Добавляем отступы */}
            <Typography
              variant="h6"
              component="h2"
              sx={{ fontWeight: 'bold', mb: 1 }} // Добавляем отступ снизу для заголовка
            >
              Заказ{' '}
              <Box component="span" sx={{ fontWeight: 'bold' }}>
                #{orderData.id}
              </Box>
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 1 }} // Добавляем отступ снизу
            >
              <Box component="span" sx={{ fontWeight: 'bold' }}>
                Заказ от:
              </Box>{' '}
              {orderData.date_create}
            </Typography>
            {orderData.date_of_arraving && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 1 }} // Добавляем отступ снизу
              >
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Дата прибытия:
                </Box>{' '}
                {orderData.date_of_arraving}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              mt: isMobile ? 2 : 0,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2,
              alignItems: isMobile ? 'flex-start' : 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                backgroundColor: theme.palette.grey[200],
                px: 2,
                py: 1,
                borderRadius: 1,
                fontWeight: 'bold',
              }}
            >
              <Box component="span" sx={{ fontWeight: 'bold' }}>
                Статус:
              </Box>{' '}
              {orderData.status}
            </Typography>

            <Button
              variant="contained"
              color="success"
              startIcon={<Download />}
              onClick={async () => {
                const response = await api.get(orderData.excelUrl, {
                  responseType: 'blob', // Важно для бинарных данных (Excel, PDF и т. д.)
                })

                // Создаем Blob из полученных данных
                const blob = new Blob([response.data])

                // Создаем ссылку для скачивания с фиксированным именем файла
                const downloadUrl = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = downloadUrl
                link.download = 'output.xlsx' // Всегда используем это имя файла
                document.body.appendChild(link)
                link.click() // Имитируем клик

                // Очищаем память
                document.body.removeChild(link)
                window.URL.revokeObjectURL(downloadUrl)
              }}
              // href={orderData.excelUrl}
              sx={{
                whiteSpace: 'nowrap',
                mt: isMobile ? 1 : 0,
              }}
            >
              Получить Excel
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="body1">
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              Итог:
            </Box>{' '}
            {orderData.total} ₽
          </Typography>
          <Typography variant="body1">
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              Дата поступления:
            </Box>{' '}
            {orderData.date_of_arraving}
          </Typography>
        </Box>
      </Paper>

      <Paper elevation={3} sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table aria-label="order items table">
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Фото</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Название</TableCell>
                {true && (
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Кол-во
                  </TableCell>
                )}
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Стоимость
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Итог
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderData.items.length > 0 ? (
                orderData.items.map((item) => (
                  <TableRow key={item.id} onClick={() => handleRowClick(item)}>
                    <TableCell>
                      <Box
                        component="img"
                        src={`${API_URL}/media/${item.image}`}
                        alt={item.name}
                        sx={{
                          width: 50,
                          height: 50,
                          objectFit: 'cover',
                          borderRadius: 1,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {item.name}
                      </Typography>
                    </TableCell>
                    {true && (
                      <TableCell align="center">
                        <Typography>{item.quantity}</Typography>
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Typography>{item.price} ₽</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {item.total} ₽
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isMobile ? 4 : 5} align="center">
                    <Typography>Товары отсутствуют</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Модальное окно */}
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="product-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '95%' : 800,
            maxWidth: '95vw',
            maxHeight: '95vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            overflow: 'auto',
          }}
        >
          <Grid container>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: isMobile ? 2 : 4,
                backgroundColor: '#fafafa',
              }}
            >
              <CardMedia
                component="img"
                image={`${API_URL}/media/${product?.image}`}
                alt={product?.name}
                sx={{
                  maxHeight: 400,
                  width: '100%',
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                p: isMobile ? 2 : 4,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h5" gutterBottom fontWeight="bold">
                {product?.name}
              </Typography>
              {product.size && (
                <Typography variant="body1" color="text.secondary" mb={2}>
                  <strong>Размер:</strong> {product.size}
                </Typography>
              )}
              {product.size_2 && (
                <Typography variant="body1" color="text.secondary" mb={2}>
                  <strong>Размер №2:</strong> {product.size_2}
                </Typography>
              )}
              {product.size_3 && (
                <Typography variant="body1" color="text.secondary" mb={2}>
                  <strong>Качество: </strong> {product.size_3}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </Box>
  )
}

export default OrderHistoryElement
